import { i18n } from '../../../i18n/index.js';
// Определяет сущность, открытую сейчас на экране, — к ней быстрая заметка
// привязывается по умолчанию. Название подтягиваем из уже существующих запросов
// (react-query дедуплицирует, дополнительной нагрузки нет).
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { getCompanyById, getCompanyDetailQueryKey } from '../../companies/api/companies.client.js';
import { getContactById, getContactDetailQueryKey } from '../../contacts/api/contacts.client.js';
import { getDealById, getDealDetailQueryKey } from '../../deals/api/deals.client.js';
import type { NoteLinkInput } from '../api/notes.client.js';

export type EntityKind = 'deal' | 'contact' | 'company' | null;

export interface EntityContext {
  kind: EntityKind;
  id: string | null;
  /** Человекочитаемое имя для подписи кнопки; null пока грузится. */
  title: string | null;
  typeLabel: string;
  /** Готовая привязка для createNote. */
  link: NoteLinkInput;
}

const TYPE_LABEL: Record<Exclude<EntityKind, null>, string> = {
  get deal() {
    return i18n.t('crm.features.map.features.notes.hooks.usecurrententitycontext.69');
  },
  get contact() {
    return i18n.t('crm.features.map.features.notes.hooks.usecurrententitycontext.70');
  },
  get company() {
    return i18n.t('crm.features.map.features.notes.hooks.usecurrententitycontext.71');
  },
};

/** /deals/d-o1 → {kind:'deal', id:'d-o1'}; списки и прочие страницы → null. */
function parseRoute(pathname: string): { kind: EntityKind; id: string | null } {
  const match = /^\/(deals|contacts|companies)\/([^/?#]+)/.exec(pathname);
  if (!match) return { kind: null, id: null };
  const [, section, id] = match;
  if (!id || id === 'mvp') return { kind: null, id: null };
  const kind =
    section === 'deals' ? 'deal' : section === 'contacts' ? 'contact' : ('company' as EntityKind);
  return { kind, id };
}

export function useCurrentEntityContext(): EntityContext {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const { pathname } = useLocation();
  const { kind, id } = parseRoute(pathname);

  const dealQuery = useQuery({
    queryKey: getDealDetailQueryKey(workspaceId, id ?? 'none'),
    queryFn: () => getDealById(id as string),
    enabled: scope !== null && kind === 'deal' && id !== null,
  });
  const contactQuery = useQuery({
    queryKey: getContactDetailQueryKey(workspaceId, id ?? 'none'),
    queryFn: () => getContactById(id as string),
    enabled: scope !== null && kind === 'contact' && id !== null,
  });
  const companyQuery = useQuery({
    queryKey: getCompanyDetailQueryKey(workspaceId, id ?? 'none'),
    queryFn: () => getCompanyById(id as string),
    enabled: scope !== null && kind === 'company' && id !== null,
  });

  if (kind === null || id === null) {
    return { kind: null, id: null, title: null, typeLabel: '', link: {} };
  }

  const title =
    kind === 'deal'
      ? (dealQuery.data?.title ?? null)
      : kind === 'contact'
        ? [contactQuery.data?.firstName, contactQuery.data?.lastName].filter(Boolean).join(' ') ||
          null
        : (companyQuery.data?.name ?? null);

  const link =
    kind === 'deal' ? { dealId: id } : kind === 'contact' ? { contactId: id } : { companyId: id };

  return { kind, id, title, typeLabel: TYPE_LABEL[kind], link };
}
